import { z } from 'zod/v4';

import {
    FONT_TAGS_ORDER,
    HIDDEN_FONT_TAGS,
    THEME_BREAKPOINTS,
    ThemeFontFamilies,
    ThemeFontStyle,
    ThemeTextTransform,
} from '@sites/site-theme';
import type { ThemeFontTags } from '@sites/site-theme';

import { CSS_RESTRICTIONS } from '@common/constants/css-restrictions.const';

import { firstDefined, normalizeFlatArgs, parseIfJsonText } from '../widget-schemas/fold-helpers';

/**
 * AI-facing schema for `update_theme_font` — one text tag's typography, site-wide.
 *
 * **One tag per call, not the whole set.** A theme carries eleven visible tags, and a request is
 * almost always about one of them ("make the headings bigger", "use Roboto for body text"). A tool
 * taking all eleven would put ten unchanged tags into every call for the model to copy correctly,
 * which is exactly the kind of bulk transcription it gets wrong. Several tags in one go are still
 * one round trip — `apply_operations` batches them.
 *
 * **Sizes are bare numbers in the unit the panel edits them in** — px for `fontSize` and
 * `letterSpacing`, % for `lineHeight` — and the stored `{ value, unit }` is assembled server-side.
 * The unit is a property of the property, not a choice: the settings panel's inputs assume it, and
 * letting a caller send `"1.5rem"` would produce a theme the panel cannot show.
 */

/**
 * The tags a caller may name. `pre` is excluded: it follows body copy rather than being set.
 *
 * `HIDDEN_FONT_TAGS` is declared `as const`, so its element type is the single literal it holds and
 * `.includes()` would only accept that literal. Widened once here rather than cast at the call: the
 * list is a vocabulary, and reading it against another tag is exactly what it is for.
 */
const HIDDEN_TAGS: readonly ThemeFontTags[] = HIDDEN_FONT_TAGS;

export const THEME_FONT_TAGS = FONT_TAGS_ORDER.filter((tag) => !HIDDEN_TAGS.includes(tag));

const THEME_FONT_FAMILIES = Object.values(ThemeFontFamilies);

/** `'light' | 'normal' | 'bold'` → the numbers stored, so a model writing words is not refused. */
const NAMED_WEIGHTS: Readonly<Record<string, number>> = { light: 300, normal: 400, regular: 400, bold: 700 };

const updateThemeFontObjectSchema = z.object({
    tag: z
        .enum(THEME_FONT_TAGS as [ThemeFontTags, ...ThemeFontTags[]])
        .describe(
            'Which text style to change. h1–h4 are headings, p is body copy, button is button labels, ' +
                'leading/big/small/quote/signature are the remaining text styles.'
        ),
    font: z
        .enum(THEME_FONT_FAMILIES as [ThemeFontFamilies, ...ThemeFontFamilies[]])
        .optional()
        .describe(
            'Font family, from this exact list. Applies to every device — a tag cannot have one family ' +
                'per breakpoint. Required when the tag has no typography stored yet.'
        ),
    fontSize: z
        .number()
        .min(CSS_RESTRICTIONS.FONT_SIZE_MIN)
        .max(CSS_RESTRICTIONS.FONT_SIZE_MAX)
        .optional()
        .describe(`Size in PIXELS, ${CSS_RESTRICTIONS.FONT_SIZE_MIN}–${CSS_RESTRICTIONS.FONT_SIZE_MAX}.`),
    fontWeight: z
        .number()
        .optional()
        .describe('Weight as a number: 300 light, 400 normal, 700 bold. Not every family offers all three.'),
    lineHeight: z
        .number()
        .min(CSS_RESTRICTIONS.LINE_HEIGHT_MIN)
        .max(CSS_RESTRICTIONS.LINE_HEIGHT_MAX)
        .optional()
        .describe(
            `Line height as a PERCENTAGE of the font size, ${CSS_RESTRICTIONS.LINE_HEIGHT_MIN}–` +
                `${CSS_RESTRICTIONS.LINE_HEIGHT_MAX} — 140 means 1.4×.`
        ),
    letterSpacing: z
        .number()
        .min(CSS_RESTRICTIONS.LETTER_SPACING_MIN)
        .max(CSS_RESTRICTIONS.LETTER_SPACING_MAX)
        .optional()
        .describe(
            `Extra space between letters, in PIXELS, ${CSS_RESTRICTIONS.LETTER_SPACING_MIN}–` +
                `${CSS_RESTRICTIONS.LETTER_SPACING_MAX}.`
        ),
    textTransform: z
        .enum(ThemeTextTransform)
        .optional()
        .describe('"uppercase" renders the tag in capitals; "none" leaves the text as written.'),
    fontStyle: z.enum(ThemeFontStyle).optional().describe('"italic" or "normal".'),
    breakpoint: z
        .enum(THEME_BREAKPOINTS)
        .optional()
        .describe(
            'Set the size-like properties for ONE device only, leaving the others as they are. Omit to set ' +
                'them for every device, which is what an ordinary "make the headings bigger" means. Family ' +
                'and weight ignore this — they are shared across devices.'
        ),
});

export type UpdateThemeFontArgs = z.infer<typeof updateThemeFontObjectSchema>;

/**
 * Lifts the shapes a model reaches for instead of the flat one.
 *
 * `{ styles: { fontSize: 32 } }` / `{ data: … }` — the widget tools all take a nested `data`, so a
 * model carries the habit over. `size`/`weight`/`family` are the everyday words for three of these
 * fields, and `device` for the breakpoint. A weight written as `"bold"` becomes 700 here rather than
 * being refused by the number schema, which would cost the whole call over a word the panel itself
 * offers as a label.
 */
function foldFont(args: Record<string, unknown>): void {
    for (const key of ['styles', 'data', 'font_settings', 'typography'] as const) {
        // Through `parseIfJsonText` because the leaked-XML transport has no types: a nested object
        // arrives as the STRING `'{"fontSize":32}'`. The palette tool lost two round trips to
        // exactly this on 2026-08-25 before it was fixed there; the gap was the same here.
        const nested = parseIfJsonText(args[key]);

        if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
            for (const [nestedKey, value] of Object.entries(nested as Record<string, unknown>)) {
                if (args[nestedKey] === undefined) {
                    args[nestedKey] = value;
                }
            }
        }

        delete args[key];
    }

    const synonyms: Readonly<Record<string, readonly string[]>> = {
        tag: ['fontTag', 'textStyle', 'style'],
        font: ['fontFamily', 'family'],
        fontSize: ['size'],
        fontWeight: ['weight'],
        breakpoint: ['device', 'deviceMode'],
    };

    for (const [target, keys] of Object.entries(synonyms)) {
        if (args[target] === undefined) {
            const value = firstDefined(...keys.map((key) => args[key]));

            if (value !== undefined) {
                args[target] = value;
            }
        }

        for (const key of keys) {
            delete args[key];
        }
    }

    const weight = args['fontWeight'];

    if (typeof weight === 'string') {
        const named = NAMED_WEIGHTS[weight.trim().toLowerCase()];

        // Left in place when it is not a word we know: the schema then names the field, which is
        // more useful than this fold quietly dropping it.
        if (named !== undefined) {
            args['fontWeight'] = named;
        }
    }
}

export const updateThemeFontArgsSchema = z.preprocess(
    (raw) => normalizeFlatArgs(raw, foldFont),
    updateThemeFontObjectSchema
);
